import { EditIndicator } from '@components/Card2/EditIndicator';
import { LockIndicator } from '@components/Card2/LockIndicator';
import { cardEditContext } from '@context/card-edit-context';
import { getOrCreateContext } from '@context/singleton-manager';
import { Draggable } from '@hello-pangea/dnd';
import type { Maybe } from 'graphql/jsutils/Maybe';
import { type KeyboardEvent, type PropsWithChildren, useContext, useMemo, useRef, useState } from 'react';

import cardFieldStyles from '../CardField.module.scss';
import { DragHandle } from './DragHandle';

const outlineContext = getOrCreateContext('card-outline');

type FieldRowProps = {
  id: string;
  fieldValue: string;
  index: number;
  isLocked?: boolean;
  isLockedTooltipMessage?: string;
  isOptional?: Maybe<boolean>;
  fieldRef: (instance: HTMLDivElement | HTMLButtonElement | null) => void;
  fieldBtnRef: (instance: HTMLDivElement | HTMLButtonElement | null) => void;
  isDragDisabled?: boolean;
  onClick?: () => void;
};

export const FieldRow = ({
  id,
  fieldValue,
  index,
  isLocked = false,
  isLockedTooltipMessage,
  isOptional,
  children,
  fieldBtnRef,
  fieldRef,
  isDragDisabled,
}: PropsWithChildren<FieldRowProps>) => {
  const [showEditIcon, setShowEditIcon] = useState(false);

  const { activeId, requestToMakeActive, makeInactive, unlock: unlockOutline } = useContext(outlineContext);
  const {
    activeAttributeId,
    requestToMakeActive: requestToEdit,
    showRequiredAttributesErrors,
  } = useContext(cardEditContext);

  const shouldMakeInactiveRef = useRef(false);

  const isActive = activeId == id;
  const showHoverStateOnActiveAttribute = activeAttributeId === id;

  function handleMouseEnter() {
    shouldMakeInactiveRef.current = false;
    requestToMakeActive(id);
    setShowEditIcon(true);
  }

  function handleFocus() {
    shouldMakeInactiveRef.current = false;
    requestToMakeActive(id);
  }

  function handleBlur() {
    makeInactive(id);
  }

  function handleMouseLeave() {
    shouldMakeInactiveRef.current = true;
    setShowEditIcon(false);
    setTimeout(() => {
      if (shouldMakeInactiveRef.current) makeInactive(id);
    }, 300);
  }

  function handleFieldClick() {
    if (isLocked) {
      return;
    }

    unlockOutline({ command: 'activate-last-request' });
    requestToEdit(id, 'update');
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLButtonElement | HTMLDivElement>) => {
    // This must be keyDown otherwise submitting the form in the edit popover will reopen the edit popover
    // if field is fully locked, user should not be able to enter key into it
    if (event.key === 'Enter' && !isLocked) {
      requestToEdit(id, 'update');
    }
  };

  const cardFieldStyle = useMemo(() => {
    if (showHoverStateOnActiveAttribute) return cardFieldStyles['card-field-active'];

    if (showRequiredAttributesErrors && !fieldValue && !isOptional)
      return cardFieldStyles['card-field-error'];

    return cardFieldStyles['card-field'];
  }, [showRequiredAttributesErrors, fieldValue, showHoverStateOnActiveAttribute, isOptional]);

  // The edit icon is outside the card-field outline, otherwise it gets distorted by the outline
  // as it morphs when changing which field is highlighted
  return (
    <div className={cardFieldStyles['field-row-container']}>
      <Draggable isDragDisabled={isDragDisabled} draggableId={id} index={index}>
        {(provided, snapshot) => {
          return (
            <div
              className={cardFieldStyle}
              data-required-error={showRequiredAttributesErrors && !fieldValue && !isOptional}
              onMouseEnter={handleMouseEnter}
              onMouseLeave={handleMouseLeave}
              onFocus={handleFocus}
              onBlur={handleBlur}
              onKeyDown={handleKeyDown}
              onClick={handleFieldClick}
              tabIndex={0}
              role="button"
              ref={(element) => {
                provided.innerRef(element);
                fieldRef(element);
                fieldBtnRef(element);
              }}
              {...provided.draggableProps}
              {...provided.dragHandleProps}
            >
              <div
                data-id={id}
                data-is-locked={isLocked}
                data-edit-mode={true}
                className={cardFieldStyles['card-field-btn']}
                ref={fieldBtnRef}
              >
                {isLocked && isActive && <LockIndicator tooltipMessage={isLockedTooltipMessage} />}

                {children}
              </div>

              {!isLocked && showEditIcon && <EditIndicator />}

              {(isActive || snapshot.isDragging) && !isDragDisabled && <DragHandle isActive={true} />}
            </div>
          );
        }}
      </Draggable>
    </div>
  );
};
